import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { TypedDateInput } from "@/components/TypedDateInput";
import { formatInformationalStatus } from "@/components/InformationalStatusText";
import { PermitCalculatedBadge, todayISO } from "@/components/PermitCalculatedBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const LOCATE_CONDITIONS = ["valid", "trouble", "remark"];

const createDraft = (record) => ({
  reference_number: record.reference_number || "",
  request_date: record.request_date || "",
  expires_on: record.expires_on || "",
  received_at: record.received_at || "",
  does_not_expire: Boolean(record.does_not_expire),
  status: record.status || "missing",
  notes: record.notes || "",
  ticket_not_needed: Boolean(record.ticket_not_needed),
  ticket_description: record.ticket_description || "",
});

export const ProjectPermitLocateDrawer = ({
  canHumanAuthority,
  onClose,
  onSaved,
  project,
  record,
}) => {
  const [draft, setDraft] = useState(() => createDraft(record));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isLocate = record.permit_type !== "Permit";
  const isNew = !record.id;

  useEffect(() => {
    setDraft(createDraft(record));
    setError("");
  }, [record]);

  const update = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const payload = {
        permit_type: record.permit_type,
        reference_number: draft.reference_number.trim(),
        request_date: draft.request_date,
        expires_on: draft.expires_on,
        notes: draft.notes.trim(),
        ticket_not_needed: draft.ticket_not_needed,
        ticket_description: draft.ticket_description.trim(),
      };
      if (isLocate) {
        // 811/Locate keeps a human operational condition.
        payload.status = draft.status;
      } else {
        // Permit: humans record only the received fact; JFM calculates readiness.
        payload.received_at = draft.received_at;
        payload.does_not_expire = draft.does_not_expire;
        if (draft.does_not_expire) payload.expires_on = "";
      }
      if (isNew) {
        await axios.post(`${API}/permits`, { ...payload, project_id: project.id });
      } else {
        await axios.patch(`${API}/permits/${record.id}`, payload);
      }
      toast.success(`${record.permit_type} saved.`);
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Record could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const changeActivity = async (action) => {
    if (!window.confirm(`Confirm: ${action} this ${record.permit_type}?`)) return;
    try {
      await axios.post(`${API}/permits/${record.id}/${action}`);
      toast.success(`${record.permit_type} ${action}d.`);
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Record could not be updated.");
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="project-record-drawer" data-testid="project-permit-locate-drawer">
        <SheetHeader>
          <SheetTitle>{isNew ? "Add" : "Edit"} {record.permit_type}</SheetTitle>
          <SheetDescription data-testid="project-permit-locate-fixed-project">
            {project.project_code} · {project.name}
          </SheetDescription>
        </SheetHeader>
        <form className="project-record-drawer-form" onSubmit={save}>
          {error && (
            <p
              className="project-record-drawer-error"
              data-testid="project-permit-locate-error"
            >
              {error}
            </p>
          )}
          <label htmlFor="project-permit-locate-reference-number">
            Reference / ticket number
            <input
              data-testid="project-permit-locate-reference-number"
              id="project-permit-locate-reference-number"
              onChange={(event) => update("reference_number", event.target.value)}
              value={draft.reference_number}
            />
          </label>
          {isLocate ? (
            <label htmlFor="project-permit-locate-status">
              Locate condition
              <select
                data-testid="project-permit-locate-status"
                id="project-permit-locate-status"
                onChange={(event) => update("status", event.target.value)}
                value={draft.status}
              >
                {!LOCATE_CONDITIONS.includes(draft.status) && (
                  <option value={draft.status}>{formatInformationalStatus(draft.status)}</option>
                )}
                <option value="valid">Valid</option>
                <option value="trouble">Trouble</option>
                <option value="remark">Remark</option>
              </select>
            </label>
          ) : (
            <div className="project-permit-received" data-testid="project-permit-received-block">
              <label htmlFor="project-permit-received-on">
                Received on
                <TypedDateInput
                  data-testid="project-permit-received-on"
                  id="project-permit-received-on"
                  onChange={(value) => update("received_at", value)}
                  value={draft.received_at}
                />
              </label>
              <button
                className="board-refresh-button project-permit-mark-received"
                data-testid="project-permit-mark-received"
                onClick={() => update("received_at", todayISO())}
                type="button"
              >
                Mark received today
              </button>
              <PermitCalculatedBadge
                readiness={project.readiness}
                testId="project-permit-calculated-status"
              />
            </div>
          )}
          <label htmlFor="project-permit-locate-request-date">
            Request date
            <TypedDateInput
              data-testid="project-permit-locate-request-date"
              id="project-permit-locate-request-date"
              onChange={(value) => update("request_date", value)}
              value={draft.request_date}
            />
          </label>
          <label htmlFor="project-permit-locate-expiration-date">
            {isLocate ? "Expiration date, if known" : "Expiration date"}
            <TypedDateInput
              data-testid="project-permit-locate-expiration-date"
              disabled={!isLocate && draft.does_not_expire}
              id="project-permit-locate-expiration-date"
              onChange={(value) => update("expires_on", value)}
              value={draft.does_not_expire && !isLocate ? "" : draft.expires_on}
            />
          </label>
          {!isLocate && (
            <label className="project-record-check" htmlFor="project-permit-does-not-expire">
              <input
                checked={draft.does_not_expire}
                data-testid="project-permit-does-not-expire"
                id="project-permit-does-not-expire"
                onChange={(event) => {
                  const checked = event.target.checked;
                  setDraft((current) => ({ ...current, does_not_expire: checked, expires_on: checked ? "" : current.expires_on }));
                }}
                type="checkbox"
              />
              This permit does not expire
            </label>
          )}
          {isLocate && (
            <label
              className="project-record-check"
              htmlFor="project-permit-locate-ticket-not-needed"
            >
              <input
                checked={draft.ticket_not_needed}
                data-testid="project-permit-locate-ticket-not-needed"
                id="project-permit-locate-ticket-not-needed"
                onChange={(event) => update("ticket_not_needed", event.target.checked)}
                type="checkbox"
              />
              <span>Locate / 811 ticket not needed</span>
            </label>
          )}
          {isLocate && (
            <label htmlFor="project-permit-locate-ticket-description">
              Ticket description
              <textarea
                data-testid="project-permit-locate-ticket-description"
                id="project-permit-locate-ticket-description"
                onChange={(event) => update("ticket_description", event.target.value)}
                value={draft.ticket_description}
              />
            </label>
          )}
          <label htmlFor="project-permit-locate-notes">
            Notes
            <textarea
              data-testid="project-permit-locate-notes"
              id="project-permit-locate-notes"
              onChange={(event) => update("notes", event.target.value)}
              value={draft.notes}
            />
          </label>
          <SheetFooter>
            {canHumanAuthority && (
              <button
                data-testid={`project-permit-locate-${record.active ? "deactivate" : "restore"}-${record.id}`}
                onClick={() => changeActivity(record.active ? "deactivate" : "restore")}
                type="button"
              >
                {record.active ? "Deactivate" : "Restore"}
              </button>
            )}
            <button
              className="board-refresh-button"
              data-testid="project-permit-locate-cancel-button"
              disabled={saving}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="invoice-primary-button"
              data-testid="project-permit-locate-save-button"
              disabled={saving}
              type="submit"
            >
              {saving ? "Saving…" : `Save ${record.permit_type}`}
            </button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};